// Links inside a table cell. A cell is still one string in the table's own rows (c1..c4); a link
// is written inside it as [words](address), with ` "new-tab"` after a web address that opens in a
// new tab:
//
//   Call [The Benjamin School](tel:5616265200) or see [their site](https://thebenjaminschool.org "new-tab")
//
// The Studio's cell editor writes the marker, the live table and the Studio preview render it, and
// everything that reads a cell as words (BLT's fact check, its cards) reads cellText. A marker whose
// address is not one of the four kinds below shows its words as plain text, never as a link.

import { safeHttpHref } from './newsletterShared';

export type CellLinkKind = 'page' | 'web' | 'phone' | 'email';

export interface CellPart {
  text: string;
  /** Present only on a link with a safe address. */
  href?: string;
  kind?: CellLinkKind;
  /** Web links only. */
  newTab?: boolean;
}

/** [words](address) or [words](address "new-tab"). Words hold no brackets; the address no spaces or parentheses. */
const LINK = /\[([^[\]\n]+)\]\(([^()\s]+)(\s+"new-tab")?\)/g;

const PAGE = /^\/(?!\/)[A-Za-z0-9\-._~/%#?=&]*$/;
const PHONE = /^tel:\+?[0-9]{3,15}$/;
const EMAIL = /^mailto:[^\s@<>"]+@[^\s@<>"]+\.[A-Za-z]{2,}$/;

/** Which kind of link an address is, or null when it is none of the four (it then never renders as a link). */
export function cellLinkKind(href: string): CellLinkKind | null {
  const h = href.trim();
  if (PAGE.test(h)) return 'page';
  if (PHONE.test(h)) return 'phone';
  if (EMAIL.test(h)) return 'email';
  if (safeHttpHref(h)) return 'web';
  return null;
}

/** The cell as plain words and links, in order. Adjacent plain words are joined. */
export function parseCell(cell: string): CellPart[] {
  const parts: CellPart[] = [];
  const pushText = (text: string) => {
    if (!text) return;
    const last = parts[parts.length - 1];
    if (last && !last.href) last.text += text;
    else parts.push({ text });
  };
  let at = 0;
  for (const m of Array.from(cell.matchAll(LINK))) {
    pushText(cell.slice(at, m.index));
    const [, text, href, newTab] = m;
    const kind = cellLinkKind(href);
    if (kind) parts.push({ text, href, kind, ...(kind === 'web' && newTab ? { newTab: true } : {}) });
    else pushText(text);
    at = (m.index ?? 0) + m[0].length;
  }
  pushText(cell.slice(at));
  return parts;
}

/** The words a reader sees in the cell: every link marker replaced by its words. */
export function cellText(cell: string): string {
  return cell.replace(/\[([^[\]\n]+)\]\(([^()\s]+)(\s+"new-tab")?\)/g, '$1');
}

/** Parts back to the cell string. Brackets inside linked words are dropped so the marker stays readable.
 *  Only a web link carries "new-tab"; a part whose address is none of the four kinds is written as its words. */
export function serializeCell(parts: CellPart[]): string {
  return parts
    .map((p) => {
      const kind = p.href ? cellLinkKind(p.href) : null;
      if (!p.href || !kind) return p.text;
      const words = p.text.replace(/[[\]\n]/g, '');
      if (!words) return '';
      // An address with a parenthesis or a space (a Wikipedia page) would end the marker early: those are
      // written percent-encoded, which every browser reads the same.
      const href = p.href.replace(/[()\s]/g, (c) => encodeURIComponent(c).replace('(', '%28').replace(')', '%29'));
      return `[${words}](${href}${kind === 'web' && p.newTab ? ' "new-tab"' : ''})`;
    })
    .join('');
}

/** A phone number as typed ("(561) 626-5200") to a tel: address, or null when it has too few digits. */
export function phoneHref(typed: string): string | null {
  const t = typed.trim();
  const digits = t.replace(/\D/g, '');
  if (digits.length < 3 || digits.length > 15) return null;
  return `tel:${t.startsWith('+') ? '+' : ''}${digits}`;
}

/** An email as typed to a mailto: address, or null when it is not an email. */
export function emailHref(typed: string): string | null {
  const href = `mailto:${typed.trim().replace(/^mailto:/i, '')}`;
  return EMAIL.test(href) ? href : null;
}
